import ImageFallback from "../components/imageFallback.js";
import ChannelMention from "../components/ChannelMention.js";
import MemberMention from "../components/MemberMention.js";
import RoleMention from "../components/RoleMention.js";

type MarkdownRenderableNode = string | MarkdownRenderableBlock | MarkdownRenderableNode[];

type MarkdownRenderableBlock = {
    [key: string]: unknown;
    type: string;
};

type MarkdownInviteGuild = {
    id?: unknown;
    name?: string;
    banner?: unknown;
    icon?: unknown;
};

type MarkdownInvite = {
    guild: MarkdownInviteGuild;
    approximate_member_count?: unknown;
};

export type MarkdownRendererAuthor = {
    role?: {
        color?: unknown;
    };
};

type MarkdownListItem = MarkdownRenderableNode;
type RenderedMarkdownNode = unknown;

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function readString(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function isInvite(value: unknown): value is MarkdownInvite {
    return isObject(value) && isObject((value as MarkdownInvite).guild);
}

function isMarkdownBlock(value: unknown): value is MarkdownRenderableBlock {
    return isObject(value) && typeof (value as { type?: unknown }).type === "string";
}

function hasLeadingLink(item: unknown): boolean {
    if (!Array.isArray(item))
        return false;

    const first = item[0];
    if (!isMarkdownBlock(first))
        return false;

    if (!Array.isArray(first.content))
        return false;

    const contentFirst = first.content[0];
    if (!Array.isArray(contentFirst))
        return false;

    const subContent = contentFirst[0];
    return isMarkdownBlock(subContent) && subContent.type === "link";
}

export function getDefaultColor(color?: unknown): string {
    if (typeof color !== "number")
        return "var(--primary)";

    return `rgba(${(color >> 16) & 0xff}, ${(color >> 8) & 0xff}, ${(color >> 0) & 0xff})`;
}

function getColorLightness(color: number): number {
    const rgb = color;
    const red = (rgb >> 16) & 0xff;
    const green = (rgb >> 8) & 0xff;
    const blue = (rgb >> 0) & 0xff;

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function selectTextColor(color: unknown): string {
    const luma = getColorLightness(readNumber(color) || 0);
    return luma > 50 ? "#000" : "#fff";
}

function renderTextNode(
    block: MarkdownRenderableNode,
    depth: number,
    renderBlock: (node: MarkdownRenderableNode, depth: number) => RenderedMarkdownNode,
): RenderedMarkdownNode {
    return renderBlock(block, depth + 1);
}

export function createRenderer(author: MarkdownRendererAuthor | null = null, invites: Map<string, MarkdownInvite> = new Map()) {
    return function renderBlock(block: MarkdownRenderableNode, depth = 0): RenderedMarkdownNode {
        if (typeof block === "string")
            return block;

        if (Array.isArray(block))
            return block.map((subblock) => renderBlock(subblock, depth));

        if (!isMarkdownBlock(block))
            return null;

        const mentionExtra = isObject(block.extra) ? block.extra as Record<string, unknown> : undefined;
        const memberRoleColor = readNumber(author?.role?.color);

        switch (block.type) {
            case "invite": {
                const inviteCode = readString(block.code);
                if (!inviteCode)
                    return null;

                const invite = invites.get(inviteCode);
                if (!isInvite(invite))
                    return null;

                const guild = invite.guild || {};
                const guildId = String(guild.id ?? "");
                const guildIcon = readString(guild.icon);
                const banner = readString(guild.banner);
                const iconUrl = guildId && guildIcon ? `https://cdn.discordapp.com/icons/${guildId}/${guildIcon}` : undefined;

                return invite.guild && (
                    <div style="background: rgb(28, 27, 31);border-radius: 0.25rem;max-width: 432px;overflow: hidden;">
                        {banner && (
                            <ImageFallback style="width: 100%; height: 64px; object-fit: cover; display: block;" src={`https://cdn.discordapp.com/banners/${guildId}/${banner}?size=4096`} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={readString(guild.name) ?? ""} />
                        )}

                        <div style="display: flex; align-items: center; padding: 1rem;">
                            {iconUrl && (
                                <ImageFallback style="border-radius: 1rem; width: 60px; height: 60px; margin-right: 1rem;" src={iconUrl} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={readString(guild.name) ?? ""} />
                            )}

                            <div>
                                <div style="font-weight: 600">{guild.name}</div>
                                <div style="display: inline-flex;gap: .5rem;align-items: center;margin-top: .5rem;font-size: .95rem;">
                                    <span style="background: rgba(255, 255, 255, 0.2);border-radius: 50%;width: 14px;height: 14px;" />
                                    {invite.approximate_member_count} Membres
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            case "image":
                return (
                    <img style="border-radius: 1rem; width: 100%; margin: 2rem 0; display: block;" src={readString(block.src) ?? ""} alt={readString(block.alt) ?? ""} />
                );

            case "title":
                return (
                    <h1 style="font-size: 2rem;max-width: 600px;margin-top: 2.5rem;margin-bottom: 2rem; --emoji-factor: 1.25;">
                        {renderBlock(block.content as MarkdownRenderableNode, depth + 1)}
                    </h1>
                );

            case "subtitle":
                return (
                    <h2 style="font-size: 1.05rem; max-width: 600px; margin-top: 2rem; margin-bottom: 1rem; --emoji-factor: 1.15;">
                        {renderBlock(block.content as MarkdownRenderableNode, depth + 1)}
                    </h2>
                );

            case "list":
                return (
                    <ul style={[ "display: flex; flex-direction: column; margin: 0; padding: 0 1.5rem; gap: 1rem;", depth > 0 && "gap: .5rem" ]}>
                        {Array.isArray(block.items)
                            ? block.items.map((item: MarkdownListItem) => (
                                <li style={[ "font-size: 1.1rem; color: #bfbfbf; line-height: 1.5rem;", hasLeadingLink(item) && `color: ${getDefaultColor(memberRoleColor)}` ]}>
                                    {renderBlock(item as MarkdownRenderableNode, depth + 1)}
                                </li>
                            ))
                            : null}
                    </ul>
                );

            case "text":
                return (
                    <p style="margin: 0; color: #bfbfbf; line-height: 1.25rem;">
                        {renderTextNode(block.content as MarkdownRenderableNode, depth, renderBlock)}
                    </p>
                );

            case "blockquote":
                return (
                    <blockquote style="margin: 1rem 0px;font-size: 1.1rem;color: rgb(191, 191, 191);border-left: 4px solid #545454;border-radius: .25rem;padding: 1rem;background: #1a1a1a;">
                        {renderBlock(block.content as MarkdownRenderableNode, depth + 1)}
                    </blockquote>
                );

            case "code_inline":
                return (
                    <code style="background: #1a1a1a; border-radius: 0.25rem; padding: 0.25rem; color: #969696;">
                        {renderTextNode(block.content as MarkdownRenderableNode, depth, renderBlock)}
                    </code>
                );

            case "code_block":
                return (
                    <pre>
                        <code style="padding: 1rem;border-radius: .25rem;background: rgba(255, 255, 255, .1);width: 100%;display: block;box-sizing: border-box;border: 1px solid #212121; line-height: 1.5rem;">
                            {renderTextNode(block.content as MarkdownRenderableNode, depth, renderBlock)}
                        </code>
                    </pre>
                );

            case "bold":
                return <b>{renderBlock(block.content as MarkdownRenderableNode, depth + 1)}</b>;

            case "italic":
                return <i>{renderBlock(block.content as MarkdownRenderableNode, depth + 1)}</i>;

            case "underline":
                return <u>{renderBlock(block.content as MarkdownRenderableNode, depth + 1)}</u>;

            case "strikethrough":
                return <s>{renderBlock(block.content as MarkdownRenderableNode, depth + 1)}</s>;

            case "emoji":
                return <img style="vertical-align: bottom; object-fit: contain; width: calc(var(--emoji-factor, 1) * 28px); height: calc(var(--emoji-factor, 1) * 28px); margin: 0 .5rem;" src={readString(block.url) ?? ""} alt={readString(block.name) ?? ""} />;

            case "unicode_emoji":
                return (
                    <span style={`overflow: auto; padding: 1rem;border-radius: 0.25rem;color: rgb(255, 255, 255);display: inline-flex;align-items: center;justify-content: center;aspect-ratio: 1/1;width: 8px;height: 8px;box-sizing: border-box;margin-right: .25rem;font-weight: 700;background: ${getDefaultColor(memberRoleColor)};  margin-bottom: 0.25rem;`}>
                        {String(readString(block.content) ?? "").charCodeAt(0) - 48}.
                    </span>
                );

            case "link":
                return (
                    <a href={readString(block.url)} target="_blank" style={`color: ${getDefaultColor(memberRoleColor)}; text-decoration: underline; text-overflow: ellipsis; overflow: hidden; max-width: 100%; display: inline-block; vertical-align: top;`}>
                        {renderBlock(block.content as MarkdownRenderableNode, depth + 1)}
                    </a>
                );

            case "mention": {
                const subType = readString(block.subType);
                if (!subType)
                    return null;

                if (subType === "everyone" || subType === "here")
                    return (
                        <span style={`color: #FFF;background: ${getDefaultColor()};border-radius: .25rem;padding: .25rem;font-weight: 600;font-size: 1rem;`}>
                            @{subType}
                        </span>
                    );

                const prefix = subType === "channel" ? "#" : "@";

                if (mentionExtra) {
                    if (subType === "user")
                        return (
                            <MemberMention id={readString(block.id) ?? ""} fallbackLabel={readString(mentionExtra.name) ?? null}
                            />
                        );
                }

                if (subType === "role")
                    return <RoleMention id={readString(block.id) ?? ""} fallbackLabel={readString(mentionExtra?.name) ?? null} />;

                if (subType === "channel")
                    return <ChannelMention id={readString(block.id) ?? ""} fallbackLabel={readString(mentionExtra?.name) ?? null} />;

                if (mentionExtra)
                    return (
                        <span style={`color: ${selectTextColor(mentionExtra.color)};background: ${getDefaultColor(mentionExtra.color)};border-radius: .25rem;padding: .25rem;font-weight: 600;font-size: 1rem;`}>
                            {prefix}{readString(mentionExtra.name)}
                        </span>
                    );

                return (
                        subType === "user"
                        ? <MemberMention id={readString(block.id) ?? ""} />
                        : (
                            <span style="background: rgb(26, 26, 26); border-radius: 0.25rem; padding: 0.25rem; color: rgb(150, 150, 150);">
                                {prefix}{readString(block.id)}
                            </span>
                        )
                );
            }

            case "hardbreak":
                return <br/>;

            default:
                console.warn(block);
                return null;
        }
    };
}

export function renderMarkdownAST(markdownAST: unknown): RenderedMarkdownNode {
    return createRenderer()(markdownAST as MarkdownRenderableNode);
}
